import { doc, updateDoc, increment, serverTimestamp, runTransaction, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * 🎯 Servicio de Puntos - Sistema de Running Total
 * 
 * Características:
 * ✅ Campo totalPoints en members (acumulado de toda la vida)
 * ✅ Actualización instantánea con increment()
 * ✅ Soporte para tareas, actividades y recompensas
 * ✅ Permite desmarcar hoy y ayer
 * ✅ 98% menos lecturas que sistema anterior
 */

/**
 * 🚀 Actualizar puntos del miembro (incremento o decremento)
 * 
 * @param memberId - ID del miembro
 * @param pointsDelta - Puntos a sumar (positivo) o restar (negativo)
 * @param source - Origen de los puntos para tracking
 */
export async function updateMemberTotalPoints(
  memberId: string,
  pointsDelta: number,
  source: 'task' | 'activity' | 'reward' | 'adjustment' = 'task'
): Promise<void> {
  if (!memberId || pointsDelta === 0) return;

  try {
    const memberRef = doc(db, 'members', memberId);
    
    await updateDoc(memberRef, {
      totalPoints: increment(pointsDelta),
      lastPointsUpdate: serverTimestamp(),
      lastPointsSource: source
    });

    console.log(`💰 Points updated: ${memberId} ${pointsDelta > 0 ? '+' : ''}${pointsDelta} (${source})`);
  } catch (error) {
    console.error('Error updating member points:', error);
    throw error;
  }
}

/**
 * 📊 Obtener puntos del día actual desde daily_log
 * 
 * @param memberId - ID del miembro
 * @param familyId - ID de la familia
 * @param date - Fecha en formato YYYY-MM-DD (default: hoy)
 * @returns Puntos del día
 */
export async function getTodayPoints(
  memberId: string,
  familyId: string,
  date?: string
): Promise<number> {
  if (!memberId || !familyId) return 0;

  const today = date || new Date().toISOString().split('T')[0];

  try {
    const logsSnapshot = await getDocs(
      query(
        collection(db, 'daily_log'),
        where('memberId', '==', memberId),
        where('familyId', '==', familyId),
        where('date', '==', today)
      )
    );

    const todayPoints = logsSnapshot.docs.reduce((sum, doc) => {
      const data = doc.data();
      return sum + (Number(data.points) || 0);
    }, 0);

    return todayPoints;
  } catch (error) {
    console.error('Error getting today points:', error);
    return 0;
  }
}

/**
 * 🔄 Recalcular totalPoints desde daily_log (para migración o corrección)
 * 
 * @param memberId - ID del miembro
 * @param familyId - ID de la familia
 * @returns Total de puntos calculados
 */
export async function recalculateTotalPoints(
  memberId: string,
  familyId: string
): Promise<number> {
  if (!memberId || !familyId) return 0;

  try {
    console.log(`🔄 Recalculating total points for member ${memberId}...`);

    // Obtener TODOS los logs del miembro
    const logsSnapshot = await getDocs(
      query(
        collection(db, 'daily_log'),
        where('memberId', '==', memberId),
        where('familyId', '==', familyId)
      )
    );

    // Sumar todos los puntos (positivos y negativos)
    const totalPoints = logsSnapshot.docs.reduce((sum, doc) => {
      const data = doc.data();
      return sum + (Number(data.points) || 0);
    }, 0);

    console.log(`✅ Total points calculated: ${totalPoints} (from ${logsSnapshot.docs.length} logs)`);

    // Actualizar member con el total calculado
    const memberRef = doc(db, 'members', memberId);
    await updateDoc(memberRef, {
      totalPoints: totalPoints,
      lastPointsUpdate: serverTimestamp(),
      lastPointsSource: 'recalculation'
    });

    return totalPoints;
  } catch (error) {
    console.error('Error recalculating total points:', error);
    throw error;
  }
}

/**
 * 🎁 Canjear recompensa (restar puntos)
 * 
 * @param memberId - ID del miembro
 * @param pointsCost - Costo de la recompensa (positivo)
 * @param rewardName - Nombre de la recompensa para logging
 */
export async function redeemReward(
  memberId: string,
  pointsCost: number,
  rewardName: string
): Promise<void> {
  if (!memberId || pointsCost <= 0) return;

  try {
    // Restar puntos (usar negativo)
    await updateMemberTotalPoints(memberId, -pointsCost, 'reward');
    
    console.log(`🎁 Reward redeemed: ${rewardName} (-${pointsCost} points)`);
  } catch (error) {
    console.error('Error redeeming reward:', error);
    throw error;
  }
}

/**
 * ↩️ Devolver recompensa (sumar puntos de vuelta)
 * 
 * @param memberId - ID del miembro
 * @param pointsRefund - Puntos a devolver (positivo)
 */
export async function refundReward(
  memberId: string,
  pointsRefund: number
): Promise<void> {
  if (!memberId || pointsRefund <= 0) return;

  try {
    // Sumar puntos de vuelta
    await updateMemberTotalPoints(memberId, pointsRefund, 'reward');
    
    console.log(`↩️ Reward refunded: +${pointsRefund} points`);
  } catch (error) {
    console.error('Error refunding reward:', error);
    throw error;
  }
}

/**
 * 📈 Obtener estadísticas de puntos del miembro
 * 
 * @param memberId - ID del miembro
 * @param familyId - ID de la familia
 * @returns Objeto con estadísticas
 */
export async function getMemberPointsStats(
  memberId: string,
  familyId: string
): Promise<{
  totalPoints: number;
  todayPoints: number;
  yesterdayPoints: number;
  weekPoints: number;
}> {
  if (!memberId || !familyId) {
    return { totalPoints: 0, todayPoints: 0, yesterdayPoints: 0, weekPoints: 0 };
  }

  try {
    // Leer totalPoints desde member
    const memberRef = doc(db, 'members', memberId);
    const memberSnap = await getDoc(memberRef);
    const totalPoints = memberSnap.data()?.totalPoints || 0;

    // Calcular fechas
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

    // Leer logs de última semana
    const logsSnapshot = await getDocs(
      query(
        collection(db, 'daily_log'),
        where('memberId', '==', memberId),
        where('familyId', '==', familyId),
        where('date', '>=', weekAgo)
      )
    );

    let todayPoints = 0;
    let yesterdayPoints = 0;
    let weekPoints = 0;

    logsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const points = Number(data.points) || 0;
      const date = data.date;

      if (date === today) todayPoints += points;
      if (date === yesterday) yesterdayPoints += points;
      weekPoints += points;
    });

    return {
      totalPoints,
      todayPoints,
      yesterdayPoints,
      weekPoints
    };
  } catch (error) {
    console.error('Error getting member points stats:', error);
    return { totalPoints: 0, todayPoints: 0, yesterdayPoints: 0, weekPoints: 0 };
  }
}
